import React, { useMemo } from "react"
import { graphql } from "gatsby"
import { NavigationTile } from "./NavigationTile"
import { SingleNagivationTile } from "./navigation-tile-type"
import { useShapeByRowPosition } from "./hooks/useShapeByRowPosition"

interface NavigationTilesProps {
  eyebrowText?: string
  hasAltBackgroundColor?: boolean
  heading?: string
  navTiles: SingleNagivationTile[]
  subHeading?: string
}

export const NavigationTiles = ({
  eyebrowText,
  hasAltBackgroundColor = false,
  heading,
  navTiles,
  subHeading,
}: NavigationTilesProps) => {
  // Navigation tiles are a specific type of content block that is returned by Content blocks.
  const alternateBackground = hasAltBackgroundColor
    ? "alternate-background-color"
    : ""

  const getWrapperModifierByNumberOfTiles = (length: number) => {
    const hasOnlyOneTile = length === 1
    const hasTwoTiles = length === 2
    const has3Tiles = length === 3
    const multipleOf3 = length % 3 === 0
    const sixOrMore = length >= 6
    const has5Tiles = length === 5

    switch (true) {
      case hasOnlyOneTile:
        return "nav-tile-container__wrapper--up-to-1-column"
      case hasTwoTiles:
        return "nav-tile-container__wrapper--up-to-2-column"
      case has3Tiles:
      case multipleOf3 && sixOrMore:
      case has5Tiles:
        return "nav-tile-container__wrapper--up-to-3-column"
      default:
        return "nav-tile-container__wrapper--up-to-4-column"
    }
  }

  const navTilesWithShapeBackground = useMemo(
    () =>
      navTiles.map((navTile, index) => {
        const { shape } = useShapeByRowPosition(index)

        return {
          ...navTile,
          shapeComponent: shape,
        }
      }),
    [navTiles]
  )

  return (
    <section
      className={`u-content-block-padding nav-tile-container ${alternateBackground}`}
    >
      <div className={`fr-container `}>
        {heading && (
          <div className={`u-container-header`}>
            {eyebrowText && <h4 className={`eyebrow-text`}>{eyebrowText}</h4>}
            {<h2>{heading}</h2>}
            {subHeading && <h3>{subHeading}</h3>}
          </div>
        )}

        <div
          className={`nav-tile-container__wrapper ${getWrapperModifierByNumberOfTiles(
            navTiles.length
          )}`}
        >
          {navTilesWithShapeBackground.map(
            ({ id, elements, shapeComponent }) => {
              if (!elements) return
              return (
                <NavigationTile
                  key={id}
                  tile={elements}
                  showDescription
                  backgroundShape={shapeComponent}
                />
              )
            }
          )}
        </div>
      </div>
    </section>
  )
}

export const query = graphql`
  fragment NavigationTilesFragment on kontent_item_navigation_block {
    id
    elements {
      eyebrow_text {
        value
      }
      heading {
        value
      }
      sub_heading {
        value
      }
      alt_background_color {
        value {
          codename
        }
      }
      navigation_tiles {
        modular_content {
          ...NavigationTileFragment
        }
      }
    }
  }
`
